import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'
import API from '../services/API'

// Renders one version of the AmbuNet Business Subscription Agreement.
//
// Unlike the Terms of Use and Privacy Policy, the agreement text is not
// bundled with the client: the server owns the versioned files and decides
// which version is current, so the text shown here is always the text the
// server will record an acceptance against. With no `version` (prop or route
// param) the server returns the current version.
const compactSx = {
  '& h1': { fontSize: '1.8rem', mt: 0, mb: 1.5 },
  '& h2': { fontSize: '1.35rem', mt: 2.5, mb: 1 },
  '& h3': { fontSize: '1.1rem', mt: 2, mb: 0.75 },
  '& p': { fontSize: '0.9rem', lineHeight: 1.6, mb: 1.25 },
  '& li': { fontSize: '0.9rem', lineHeight: 1.6, mb: 0.5 },
  '& ul, & ol': { pl: 3, mb: 1.25 },
  '& hr': { my: 2 },
}

const fullSx = {
  maxWidth: 900,
  mx: 'auto',
  px: 3,
  py: 4,
  '& h1': { fontSize: '2.5rem', fontWeight: 700, mb: 2, mt: 0, lineHeight: 1.2 },
  '& h2': { fontSize: '2rem', fontWeight: 700, mt: 4, mb: 2, lineHeight: 1.25 },
  '& h3': { fontSize: '1.4rem', fontWeight: 600, mt: 3, mb: 1.5, lineHeight: 1.3 },
  '& p': { fontSize: '1rem', lineHeight: 1.7, mb: 2, mt: 0 },
  '& ul, & ol': { pl: 3, mb: 2, mt: 0 },
  '& li': { mb: 1, lineHeight: 1.7 },
  '& hr': { border: 0, borderTop: '1px solid rgba(0,0,0,0.12)', my: 3 },
  '& strong': { fontWeight: 700 },
}

const SubscriptionAgreementDocument = ({ version, compact = false }) => {
  const { version: routeVersion } = useParams()
  const requestedVersion = version ?? routeVersion
  const [agreement, setAgreement] = useState(null)
  const [status, setStatus] = useState('loading')

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      // skipGlobalErrorHandler: a failed fetch must only ever show the
      // inline retry below, never swap the page out or leave it hanging.
      const res = await API.get(
        `/billing/subscription-agreement/document${
          requestedVersion ? `/${encodeURIComponent(requestedVersion)}` : ''
        }`,
        { skipGlobalErrorHandler: true },
      )
      if (res.status === 200 && res.data?.markdown) {
        setAgreement(res.data)
        setStatus('ready')
        return
      }
      setStatus(res.status === 404 ? 'notFound' : 'error')
    } catch (e) {
      setStatus('error')
    }
  }, [requestedVersion])

  useEffect(() => {
    load()
  }, [load])

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: compact ? 3 : 8 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (status !== 'ready') {
    return (
      <Box sx={{ textAlign: 'center', py: compact ? 3 : 8, px: 2 }}>
        <Typography sx={{ mb: 1.5 }}>
          {status === 'notFound'
            ? 'That version of the AmbuNet Business Subscription Agreement could not be found.'
            : 'The AmbuNet Business Subscription Agreement could not be loaded.'}
        </Typography>
        {status === 'error' && (
          <Button variant="outlined" size="small" onClick={load}>
            Try again
          </Button>
        )}
      </Box>
    )
  }

  return (
    <Box sx={compact ? compactSx : fullSx}>
      <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
        {agreement.markdown}
      </ReactMarkdown>
    </Box>
  )
}

export default SubscriptionAgreementDocument
